import React from 'react';
import { BufferMonthData } from './StrategiesUtils';
import { IMassUnitObject } from '../../../sp-types/massUnitType';
import type { TimeGranularity } from '../../../utils/timeGranularity';
import { GRANULARITY_LABELS } from '../../../utils/timeGranularity';

interface Props {
  bufferData: BufferMonthData[];
  matPrice: number;
  unit: IMassUnitObject;
  selectedMonthIndex?: number;
  timeGranularity?: TimeGranularity;
}

export const BufferMonthDetail: React.FC<Props> = ({ bufferData, matPrice, unit, selectedMonthIndex = 0, timeGranularity = 'Month' }) => {
  const isAggregated = timeGranularity !== 'Month';
  const currentMonthData = bufferData?.[selectedMonthIndex];

  if (!currentMonthData) {
    return (
      <div className="min-w-[25rem] flex flex-col flex-1 bg-white shadow-md p-4">
        <div className="text-gray-400 text-sm text-center py-8">No data available</div>
      </div>
    );
  }

  const storage = Math.max(0, currentMonthData.storage);
  const po = Math.max(0, currentMonthData.po);
  const ratioPO = Math.max(0, currentMonthData.ratioPO || 0);
  const fixedPO = Math.max(0, po - ratioPO);
  const supply = storage + po;
  const expectedDemand = currentMonthData.expectedDemand;
  const buffer = currentMonthData.buffer;
  const totalNeed = expectedDemand + buffer;

  const isGood = supply >= totalNeed;
  const shortage = isGood ? 0 : totalNeed - supply;
  const coveragePct = totalNeed > 0 ? Math.round((supply / totalNeed) * 100) : 100;
  const maxVal = Math.max(supply, totalNeed) * 1.2 || 1;

  const unitLabel = unit?.shortForm || 'mt';

  return (
    <div className="min-w-[25rem] flex flex-col flex-1 bg-white shadow-md p-4 max-h-full overflow-y-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <span style={{ color: 'rgba(59, 130, 246, 0.5)', fontSize: '0.65em' }}>&#9654;</span>
          <span className="text-base font-semibold text-gray-900">{currentMonthData.month}</span>
          <span className="text-sm text-gray-400">{isAggregated ? `${GRANULARITY_LABELS[timeGranularity]} ` : ''}Buffer</span>
        </div>
        <span className={`px-2.5 py-0.5 rounded-full text-xs font-bold ${
          isGood ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'
        }`}>
          {isGood ? '✓' : '⚠'} {coveragePct}%
        </span>
      </div>
      {isAggregated && (
        <div className="text-xs text-gray-400 mb-3 leading-tight">
          Values are aggregated across all months in this {GRANULARITY_LABELS[timeGranularity].toLowerCase()} period. Buffer shown is the end-of-period requirement.
        </div>
      )}

      {/* Comparison Bars */}
      <div className="space-y-3 mb-4">
        <div>
          <div className="text-xs font-medium text-gray-500 mb-1.5">Supply (Storage + POs)</div>
          <div className="relative h-5 rounded-md bg-gray-100 overflow-hidden">
            <div className="absolute h-full rounded-l-md" style={{ width: `${(storage / maxVal) * 100}%`, backgroundColor: '#1E3A8A' }} />
            <div className="absolute h-full" style={{ left: `${(storage / maxVal) * 100}%`, width: `${(po / maxVal) * 100}%`, backgroundColor: '#3B82F6' }} />
            <div className="absolute h-full w-0.5 z-10" style={{ left: `${(buffer / maxVal) * 100}%`, backgroundColor: '#EF4444' }} />
          </div>
        </div>

        <div>
          <div className="text-xs font-medium text-gray-500 mb-1.5">Demand + Buffer</div>
          <div className="relative h-5 rounded-md bg-gray-100 overflow-hidden">
            <div className="absolute h-full" style={{ width: `${(buffer / maxVal) * 100}%`, background: 'repeating-linear-gradient(45deg, #FCA5A5, #FCA5A5 6px, #FEE2E2 6px, #FEE2E2 12px)' }} />
            <div className="absolute h-full" style={{ left: `${(buffer / maxVal) * 100}%`, width: `${(expectedDemand / maxVal) * 100}%`, backgroundColor: '#A3A3A3' }} />
            <div className="absolute h-full w-0.5 z-10" style={{ left: `${(buffer / maxVal) * 100}%`, backgroundColor: '#EF4444' }} />
          </div>
        </div>
      </div>

      {/* Action */}
      <div className={`p-2.5 rounded-lg text-sm mb-4 ${isGood ? 'bg-green-50' : 'bg-red-50'}`}>
        {isGood ? (
          <span className="text-green-700 font-medium">✓ Buffer sufficient for {currentMonthData.month}</span>
        ) : (
          <div>
            <div className="text-red-700 font-medium">⚠ Shortage of {Math.round(shortage).toLocaleString()} {unitLabel}</div>
            <div className="text-red-600 text-xs mt-1">Consider placing purchase orders to cover the gap</div>
          </div>
        )}
      </div>

      {/* Breakdown */}
      <div className="space-y-0 divide-y divide-gray-100">
        <Row color="#1E3A8A" label="Initial Storage" qty={storage} price={matPrice} unit={unitLabel} />
        <div className="flex items-center justify-between py-1.5">
          <div className="flex items-center gap-2 min-w-0">
            <div className="w-3 h-3 rounded-sm flex-shrink-0" style={{ backgroundColor: '#3B82F6' }} />
            <div className="flex flex-col">
              <span className="text-sm text-gray-600">Supply (POs)</span>
              {ratioPO > 0 && (
                <span className="text-[11px] text-gray-400">{Math.round(fixedPO).toLocaleString()} fixed · {Math.round(ratioPO).toLocaleString()} ratio</span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-3 flex-shrink-0 ml-2">
            <span className="text-sm tabular-nums font-semibold text-gray-900">{Math.round(po).toLocaleString()} {unitLabel}</span>
            <span className="text-xs text-gray-400 w-16 text-right tabular-nums">${Math.round(po * matPrice).toLocaleString()}</span>
          </div>
        </div>
        <Row stripe="#FCA5A5" label="Buffer Stock" qty={buffer} price={matPrice} unit={unitLabel} />
        <Row color="#A3A3A3" label="Expected Demand" qty={expectedDemand} price={matPrice} unit={unitLabel} />
        <div className="pt-2">
          <Row color="#EF4444" label="Total Required" qty={totalNeed} price={matPrice} unit={unitLabel} bold />
          <div className="ml-5 text-xs text-gray-400 pb-1">Demand + Buffer</div>
        </div>
      </div>

    </div>
  );
};

const Row: React.FC<{
  color?: string;
  stripe?: string;
  label: string;
  qty: number;
  price: number;
  unit: string;
  bold?: boolean;
}> = ({ color, stripe, label, qty, price, unit, bold }) => (
  <div className="flex items-center justify-between py-1.5">
    <div className="flex items-center gap-2 min-w-0">
      <div
        className="w-3 h-3 rounded-sm flex-shrink-0"
        style={stripe
          ? { background: `repeating-linear-gradient(45deg, ${stripe}, ${stripe} 2px, ${stripe}44 2px, ${stripe}44 4px)` }
          : { backgroundColor: color }
        }
      />
      <span className={`text-sm truncate ${bold ? 'font-semibold text-gray-900' : 'text-gray-600'}`}>{label}</span>
    </div>
    <div className="flex items-center gap-3 flex-shrink-0 ml-2">
      <span className={`text-sm tabular-nums ${bold ? 'font-bold text-gray-900' : 'font-semibold text-gray-900'}`}>
        {Math.round(qty).toLocaleString()} {unit}
      </span>
      <span className="text-xs text-gray-400 w-16 text-right tabular-nums">
        ${Math.round(qty * price).toLocaleString()}
      </span>
    </div>
  </div>
);
